import React, { useState, useMemo, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Search, Download, ChevronUp, ChevronDown, ChevronsUpDown, FileText, FileImage, FileArchive, File, Trash2, Upload, X } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { ro } from 'date-fns/locale';
import { Button, Modal, EmptyState } from '@/components/common';
import { deleteAttachment, uploadWorkPackageAttachment, getAttachmentDownloadUrl, downloadAttachmentsAsZip } from '@/services/attachments.service';
import { getAuthCredentials } from '@/services/api';
import type { Attachment } from '@/types';
import { SearchableSelect } from '../common/SearchableSelect';

interface WorkPackageFilesTableProps {
  attachments: Attachment[];
  workPackageId: number;
  currentUserId?: number;
  onFileDeleted?: () => void;
  canUpload?: boolean;
}

type SortField = 'fileName' | 'createdAt' | 'fileSize' | 'author';
type SortDirection = 'asc' | 'desc' | null;

// Format file size
function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

// Get file icon based on content type
function getFileIcon(contentType: string) {
  if (contentType.startsWith('image/')) {
    return <FileImage className="w-5 h-5 text-green-500" />;
  }
  if (contentType.includes('pdf')) {
    return <FileText className="w-5 h-5 text-red-500" />;
  }
  if (contentType.includes('zip') || contentType.includes('archive') || contentType.includes('compressed')) {
    return <FileArchive className="w-5 h-5 text-yellow-500" />;
  }
  if (contentType.includes('word') || contentType.includes('document')) {
    return <FileText className="w-5 h-5 text-blue-500" />;
  }
  if (contentType.includes('excel') || contentType.includes('spreadsheet')) {
    return <FileText className="w-5 h-5 text-green-600" />;
  }
  return <File className="w-5 h-5 text-gray-500" />;
}

export function WorkPackageFilesTable({ attachments, workPackageId, currentUserId, onFileDeleted, canUpload = true }: WorkPackageFilesTableProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  // Upload state
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{ [key: string]: number }>({});
  const [error, setError] = useState<string | null>(null);

  // Delete modal state
  const [fileToDelete, setFileToDelete] = useState<Attachment | null>(null);
  const [deleteReason, setDeleteReason] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  // Bulk delete modal state
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [bulkDeleteReason, setBulkDeleteReason] = useState('');
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  // Bulk download state
  const [isDownloading, setIsDownloading] = useState(false);

  // File upload handler
  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      setIsUploading(true);
      setError(null);

      for (const file of acceptedFiles) {
        try {
          setUploadProgress((prev) => ({ ...prev, [file.name]: 0 }));
          await uploadWorkPackageAttachment(workPackageId, file);
          setUploadProgress((prev) => ({ ...prev, [file.name]: 100 }));
        } catch (err) {
          console.error('Failed to upload file:', err);
          setError(`Eroare la încărcarea fișierului ${file.name}`);
        }
      }

      setIsUploading(false);
      setUploadProgress({});
      onFileDeleted?.();
    },
    [workPackageId, onFileDeleted]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: true,
  });

  // Filter attachments by search term
  const filteredAttachments = useMemo(() => {
    if (!searchTerm.trim()) return attachments;

    const term = searchTerm.toLowerCase();
    return attachments.filter(att => {
      const authorName = att._links.author?.title || '';
      return (
        att.fileName.toLowerCase().includes(term) ||
        authorName.toLowerCase().includes(term) ||
        (att.contentType && att.contentType.toLowerCase().includes(term))
      );
    });
  }, [attachments, searchTerm]);

  // Sort attachments
  const sortedAttachments = useMemo(() => {
    if (!sortField || !sortDirection) return filteredAttachments;

    return [...filteredAttachments].sort((a, b) => {
      let aValue: string | number = '';
      let bValue: string | number = '';

      switch (sortField) {
        case 'fileName':
          aValue = a.fileName.toLowerCase();
          bValue = b.fileName.toLowerCase();
          break;
        case 'createdAt':
          aValue = a.createdAt || '';
          bValue = b.createdAt || '';
          break;
        case 'fileSize':
          aValue = a.fileSize;
          bValue = b.fileSize;
          break;
        case 'author':
          aValue = a._links.author?.title?.toLowerCase() || '';
          bValue = b._links.author?.title?.toLowerCase() || '';
          break;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredAttachments, sortField, sortDirection]);

  // Paginate
  const paginatedAttachments = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return sortedAttachments.slice(start, start + pageSize);
  }, [sortedAttachments, currentPage, pageSize]);

  const totalPages = Math.ceil(sortedAttachments.length / pageSize);

  // Handle sort
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else if (sortDirection === 'desc') {
        setSortField(null);
        setSortDirection(null);
      }
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Sort icon component
  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) {
      return <ChevronsUpDown className="w-4 h-4 text-gray-400" />;
    }
    if (sortDirection === 'asc') {
      return <ChevronUp className="w-4 h-4 text-primary-600" />;
    }
    return <ChevronDown className="w-4 h-4 text-primary-600" />;
  };

  // Handle checkbox selection
  const handleSelectAll = () => {
    if (selectedIds.size === paginatedAttachments.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(paginatedAttachments.map(a => a.id)));
    }
  };

  const handleSelectOne = (id: number) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  // Handle download using the attachment's downloadLocation
  const handleDownload = async (attachment: Attachment) => {
    try {
      const url = getAttachmentDownloadUrl(attachment);

      // For auth/files endpoints, we can open directly
      if (url.startsWith('/auth/files/')) {
        window.open(url, '_blank');
        return;
      }

      // For other endpoints, use fetch with auth
      const credentials = getAuthCredentials();
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Basic ${credentials}`,
        },
      });

      if (!response.ok) {
        throw new Error('Download failed');
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = attachment.fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(blobUrl);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Failed to download file:', err);
    }
  };

  // Handle bulk download as ZIP archive
  const handleBulkDownload = async () => {
    const selectedAttachmentIds = Array.from(selectedIds);
    if (selectedAttachmentIds.length === 0) return;

    setIsDownloading(true);
    try {
      const archiveName = `fisiere_activitate_${workPackageId}_${new Date().toISOString().split('T')[0]}.zip`;
      await downloadAttachmentsAsZip(selectedAttachmentIds, archiveName);
    } catch (err) {
      console.error('Failed to download files as ZIP:', err);
      const errorMessage = err instanceof Error ? err.message : 'Eroare la descărcarea arhivei ZIP. Încercați din nou.';
      setError(errorMessage);
    } finally {
      setIsDownloading(false);
    }
  };

  // Handle file delete
  const handleDeleteFile = async () => {
    if (!fileToDelete) return;

    setIsDeleting(true);
    try {
      await deleteAttachment(fileToDelete.id, deleteReason || undefined);
      setFileToDelete(null);
      setDeleteReason('');
      onFileDeleted?.();
    } catch (err) {
      console.error('Failed to delete file:', err);
    } finally {
      setIsDeleting(false);
    }
  };

  // Handle bulk delete
  const handleBulkDelete = async () => {
    const selectedAttachments = attachments.filter(att => selectedIds.has(att.id) && canDeleteFile(att));
    if (selectedAttachments.length === 0) return;

    setIsBulkDeleting(true);
    try {
      for (const att of selectedAttachments) {
        await deleteAttachment(att.id, bulkDeleteReason || undefined);
      }
      setSelectedIds(new Set());
      setShowBulkDeleteModal(false);
      setBulkDeleteReason('');
      onFileDeleted?.();
    } catch (err) {
      console.error('Failed to delete files:', err);
    } finally {
      setIsBulkDeleting(false);
    }
  };

  // Check if user can delete a file (only their own uploads)
  const canDeleteFile = (attachment: Attachment) => {
    if (!currentUserId) return false;
    const authorId = attachment._links.author?.href?.match(/\/users\/(\d+)/)?.[1];
    return authorId === String(currentUserId);
  };

  // Get count of deletable selected files
  const getDeletableSelectedCount = () => {
    return attachments.filter(att => selectedIds.has(att.id) && canDeleteFile(att)).length;
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    try {
      return format(parseISO(dateStr), 'dd/MM/yyyy, HH:mm');
    } catch {
      return dateStr;
    }
  };

  const isAllSelected = paginatedAttachments.length > 0 && selectedIds.size === paginatedAttachments.length;
  const hasSelection = selectedIds.size > 0;
  const deletableCount = getDeletableSelectedCount();

  return (
    <div className="space-y-4">
      {/* Upload zone */}
      {canUpload && (
        <div
          {...getRootProps()}
          className={`
            border-2 border-dashed rounded-lg p-6 mb-4 transition-colors cursor-pointer
            ${
              isDragActive
                ? 'border-primary-500 bg-primary-50'
                : 'border-gray-300 hover:border-primary-400 hover:bg-gray-50'
            }
          `}
        >
          <input {...getInputProps()} />
          <div className="text-center">
            <Upload
              className={`w-10 h-10 mx-auto mb-3 ${
                isDragActive ? 'text-primary-500' : 'text-gray-400'
              }`}
            />
            <p className="text-sm text-gray-600">
              Trageți fișierele aici sau click pentru a selecta
            </p>
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="mb-4 p-3 bg-danger-50 border border-danger-200 rounded-lg text-danger-700 text-sm flex items-center justify-between">
          <span>{error}</span>
          <button onClick={() => setError(null)}>
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Upload progress */}
      {Object.keys(uploadProgress).length > 0 && (
        <div className="mb-4 space-y-2">
          {Object.entries(uploadProgress).map(([fileName, progress]) => (
            <div key={fileName} className="p-3 bg-primary-50 rounded-lg">
              <div className="flex items-center justify-between text-sm mb-1">
                <span className="font-medium">{fileName}</span>
                <span>{progress}%</span>
              </div>
              <div className="h-1.5 bg-primary-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary-500 rounded-full transition-all"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Header cu căutare, acțiuni și dimensiune pagină */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="relative flex-1 min-w-[250px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Caută fișiere..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full pl-9 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          {hasSelection && (
            <div className="flex items-center gap-2">
              <Button
                variant="primary"
                size="sm"
                leftIcon={<Download className="w-4 h-4" />}
                onClick={handleBulkDownload}
                isLoading={isDownloading}
                disabled={isDownloading}
              >
                {isDownloading ? 'Se generează arhiva...' : `Descarcă (${selectedIds.size})`}
              </Button>
              {deletableCount > 0 && (
                <Button
                  variant="danger"
                  size="sm"
                  leftIcon={<Trash2 className="w-4 h-4" />}
                  onClick={() => setShowBulkDeleteModal(true)}
                >
                  Șterge ({deletableCount})
                </Button>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 text-sm text-gray-600">
          <span>Afișează</span>
          <SearchableSelect
            className="w-24"
            clearable={false}
            options={[
              { value: 5, label: '5' },
              { value: 10, label: '10' },
              { value: 25, label: '25' },
              { value: 50, label: '50' },
            ]}
            value={pageSize}
            onChange={(v) => {
              setPageSize(Number(v));
              setCurrentPage(1);
            }}
          />
          <span>înregistrări</span>
        </div>
      </div>

      {/* Table */}
      {attachments.length === 0 ? (
        <EmptyState
          icon={<File className="w-12 h-12" />}
          title="Nu există fișiere"
          description={canUpload ? "Trageți fișiere în zona de mai sus pentru a le încărca." : "Nu există documente atașate la această activitate."}
        />
      ) : (
        <>
          <div className="overflow-x-auto border border-gray-200 rounded-lg">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 w-10">
                    <input
                      type="checkbox"
                      checked={isAllSelected}
                      onChange={handleSelectAll}
                      className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                    />
                  </th>
                  <th
                    className="px-4 py-3 text-left font-semibold text-gray-700 cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('fileName')}
                  >
                    <div className="flex items-center gap-1">
                      Document
                      <SortIcon field="fileName" />
                    </div>
                  </th>
                  <th
                    className="px-4 py-3 text-left font-semibold text-gray-700 cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('createdAt')}
                  >
                    <div className="flex items-center gap-1">
                      Data încărcării
                      <SortIcon field="createdAt" />
                    </div>
                  </th>
                  <th
                    className="px-4 py-3 text-left font-semibold text-gray-700 cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('fileSize')}
                  >
                    <div className="flex items-center gap-1">
                      Mărime
                      <SortIcon field="fileSize" />
                    </div>
                  </th>
                  <th
                    className="px-4 py-3 text-left font-semibold text-gray-700 cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('author')}
                  >
                    <div className="flex items-center gap-1">
                      Încărcat de
                      <SortIcon field="author" />
                    </div>
                  </th>
                  <th className="px-4 py-3 text-center font-semibold text-gray-700 w-24">
                    Acțiuni
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {paginatedAttachments.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                      Nu s-au găsit rezultate pentru căutarea efectuată.
                    </td>
                  </tr>
                ) : (
                  paginatedAttachments.map((att) => {
                    const authorName = att._links.author?.title || '-';

                    return (
                      <tr
                        key={att.id}
                        className="hover:bg-blue-50 transition-colors"
                      >
                        <td className="px-4 py-3">
                          <input
                            type="checkbox"
                            checked={selectedIds.has(att.id)}
                            onChange={() => handleSelectOne(att.id)}
                            className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            {getFileIcon(att.contentType || '')}
                            <span className="font-medium text-gray-900">
                              {att.fileName}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                          {formatDate(att.createdAt)}
                        </td>
                        <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                          {formatFileSize(att.fileSize)}
                        </td>
                        <td className="px-4 py-3 text-gray-600">
                          {authorName}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDownload(att)}
                              title="Descarcă"
                            >
                              <Download className="w-4 h-4 text-primary-600" />
                            </Button>
                            {canDeleteFile(att) && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setFileToDelete(att)}
                                title="Șterge"
                              >
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Footer cu paginare */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="text-sm text-gray-600">
              Afișare {sortedAttachments.length === 0 ? 0 : (currentPage - 1) * pageSize + 1} - {Math.min(currentPage * pageSize, sortedAttachments.length)} din {sortedAttachments.length} fișiere
              {searchTerm && ` (filtrat din ${attachments.length} total)`}
            </div>

            {totalPages > 1 && (
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  className="px-2 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  &laquo;
                </button>
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-2 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  &lsaquo;
                </button>

                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum: number;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }

                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`px-3 py-1 text-sm border rounded ${
                        currentPage === pageNum
                          ? 'bg-primary-600 text-white border-primary-600'
                          : 'border-gray-300 hover:bg-gray-100'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}

                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="px-2 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  &rsaquo;
                </button>
                <button
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                  className="px-2 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  &raquo;
                </button>
              </div>
            )}
          </div>
        </>
      )}

      {/* Modal confirmare ștergere fișier */}
      <Modal
        isOpen={!!fileToDelete}
        onClose={() => {
          setFileToDelete(null);
          setDeleteReason('');
        }}
        title="Confirmare ștergere fișier"
        size="md"
        footer={
          <div className="flex justify-end gap-3">
            <Button
              variant="secondary"
              onClick={() => {
                setFileToDelete(null);
                setDeleteReason('');
              }}
            >
              Anulează
            </Button>
            <Button
              variant="danger"
              onClick={handleDeleteFile}
              isLoading={isDeleting}
              leftIcon={<Trash2 className="w-4 h-4" />}
            >
              Șterge
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <p className="text-gray-600">
            Sunteți sigur că doriți să ștergeți fișierul{' '}
            <strong>{fileToDelete?.fileName}</strong>?
          </p>
          <p className="text-sm text-gray-500">
            Această acțiune nu poate fi anulată.
          </p>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Motiv ștergere (opțional)
            </label>
            <textarea
              value={deleteReason}
              onChange={(e) => setDeleteReason(e.target.value)}
              placeholder="Introduceți motivul ștergerii..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
              rows={3}
            />
          </div>
        </div>
      </Modal>

      {/* Modal confirmare ștergere multiplă */}
      <Modal
        isOpen={showBulkDeleteModal}
        onClose={() => {
          setShowBulkDeleteModal(false);
          setBulkDeleteReason('');
        }}
        title="Confirmare ștergere fișiere"
        size="md"
        footer={
          <div className="flex justify-end gap-3">
            <Button
              variant="secondary"
              onClick={() => {
                setShowBulkDeleteModal(false);
                setBulkDeleteReason('');
              }}
            >
              Anulează
            </Button>
            <Button
              variant="danger"
              onClick={handleBulkDelete}
              isLoading={isBulkDeleting}
              leftIcon={<Trash2 className="w-4 h-4" />}
            >
              Șterge {deletableCount} fișiere
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <p className="text-gray-600">
            Sunteți sigur că doriți să ștergeți <strong>{deletableCount} fișiere</strong>?
          </p>
          <p className="text-sm text-gray-500">
            Puteți șterge doar fișierele încărcate de dumneavoastră. Această acțiune nu poate fi anulată.
          </p>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Motiv ștergere (opțional)
            </label>
            <textarea
              value={bulkDeleteReason}
              onChange={(e) => setBulkDeleteReason(e.target.value)}
              placeholder="Introduceți motivul ștergerii..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
              rows={3}
            />
          </div>
        </div>
      </Modal>
    </div>
  );
}
